import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import SEOHead from '../../components/base/SEOHead';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d663btnggmfhn7j57f20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'How can I get started with launching my business in El Paso?',
      answer: 'Begin by exploring our step-by-step guides section, which walks you through every stage from market research to launch. We recommend starting with understanding local demographics and regulations.',
    },
    {
      question: 'Are your resources free to access?',
      answer: 'Yes! All our resources, guides, templates, and information are completely free for El Paso entrepreneurs. Our mission is to support the local business community.',
    },
    {
      question: 'Do you offer one-on-one consulting services?',
      answer: 'While we provide comprehensive online resources, we can connect you with local business consultants and mentors through our networking section.',
    },
    {
      question: 'How often is the demographic data updated?',
      answer: 'We update our demographic and market data quarterly to ensure you have access to the most current information for your business planning.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact Us - Get Help Starting Your El Paso Business"
        description="Have questions about starting a business in El Paso, Texas? Contact our team for guidance on business registration, permits, funding, and local resources. We're here to help El Paso entrepreneurs succeed."
        canonical="/contact"
        keywords="contact El Paso business, business help Texas, El Paso entrepreneur support, business questions El Paso, startup assistance Texas"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <header className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">Contact Us</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Have questions about starting your business in El Paso? We're here to help. Send us a message and we'll get back to you as soon as possible.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <section>
              <div className="bg-[#FAFAF8] rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D2691E] focus:border-transparent text-sm"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D2691E] focus:border-transparent text-sm"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D2691E] focus:border-transparent text-sm"
                      >
                        <option value="">Select a subject</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Business Registration">Business Registration</option>
                        <option value="Permits & Licenses">Permits &amp; Licenses</option>
                        <option value="Funding Resources">Funding Resources</option>
                        <option value="Networking Events">Networking Events</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D2691E] focus:border-transparent text-sm resize-none"
                        placeholder="Tell us how we can help you..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.message.length}/500 characters
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D2691E] text-white py-4 rounded-lg font-semibold hover:bg-[#B8571A] transition-all shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                        <p className="text-sm text-green-800">
                          Thank you for your message! We'll get back to you within 24-48 hours.
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                        <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                        <p className="text-sm text-red-800">
                          Sorry, there was an error sending your message. Please try again.
                        </p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </section>

            <aside className="space-y-8">
              <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl p-10 text-white">
                <h2 className="text-2xl font-bold mb-6">Why Choose El Paso?</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Strategic Location</h3>
                      <p className="text-sm text-white/80">Gateway to Mexico and international markets</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-money-dollar-circle-line text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Low Cost of Living</h3>
                      <p className="text-sm text-white/80">Affordable business operations and overhead</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Diverse Workforce</h3>
                      <p className="text-sm text-white/80">Bilingual talent pool and skilled professionals</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-rocket-line text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Growing Economy</h3>
                      <p className="text-sm text-white/80">Expanding business opportunities and support</p>
                    </div>
                  </div>
                </div>
              </div>

              <nav className="bg-[#FAFAF8] rounded-2xl p-10" aria-label="Quick links">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Links</h2>
                <div className="space-y-3">
                  <a href="/guides" className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-all cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Step-by-Step Guides</span>
                    <i className="ri-arrow-right-line text-[#D2691E]" aria-hidden="true"></i>
                  </a>
                  <a href="/regulations" className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-all cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Local Regulations</span>
                    <i className="ri-arrow-right-line text-[#D2691E]" aria-hidden="true"></i>
                  </a>
                  <a href="/networking" className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-all cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Networking Events</span>
                    <i className="ri-arrow-right-line text-[#D2691E]" aria-hidden="true"></i>
                  </a>
                  <a href="/demographics" className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-all cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Demographics Data</span>
                    <i className="ri-arrow-right-line text-[#D2691E]" aria-hidden="true"></i>
                  </a>
                </div>
              </nav>
            </aside>
          </div>

          <section>
            <h2 className="text-4xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {faqs.map((faq, index) => (
                <article key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                </article>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}